/**
 * This component houses the dropzones for table fields.
 * Number of dropzones and its name is returned according to the chart type selected.
 * Once minimum number of fields are met for the given chart type, server call is made to get chart data and saved in store
 */

import { useEffect, useState } from 'react';
import {
  addOrEditSimplecardConditionalFormat,
  deleteTablecf,
  updatecfObjectOptions,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { useDispatch } from 'react-redux';
import { displayName, getLabelValues, getNameFromUid } from '../CommonFunctions/CommonFunctions';
import useChartData from './useChartData';
import SnackBar from '../SnackBar';
import LoadingPopover from '../CommonFunctions/PopOverComponents/LoadingPopover';
import { NotificationDialog, PopUpSpinner } from '../CommonFunctions/DialogComponents';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { useMemo } from 'react';
import { getMinMaxForFormattedData } from '../Charts/CrossTab/ConditionalColorFormat';
import { useSearchParams } from 'react-router-dom';

interface Props {
  tabId: number;
  tileId: number;
  screenFrom: string;
}

const ChartData = ({ tabId, tileId, screenFrom }: Props) => {
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('');
  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { chartProperties, dynamicMeasureState, token, chartControls } = useSelector(
    (state: RootState) => ({
      chartProperties: state.chartProperties,
      dynamicMeasureState: state.dynamicMeasuresState,
      token: state.isLogged.accessToken,
      chartControls: state.chartControls,
    }),
  );

  const [searchParams] = useSearchParams();
  const shareDashboardLink = searchParams.get('dashboard') || '';

  var _propKey: string = `${tabId}.${tileId}`;

  var chartProp: any =
    chartProperties.properties[_propKey].chartType === 'richText' &&
    chartProperties.properties[_propKey].isDynamicMeasureWindowOpened
      ? dynamicMeasureState.dynamicMeasureProps?.[dynamicMeasureState.selectedTabId]?.[
          dynamicMeasureState.selectedTileId
        ]?.[`${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`]
      : chartProperties.properties[_propKey];

  const getMinAndMaxValue = (column: string) => {
    const valuesArray = chartControls.properties[_propKey].chartData.map((el: any) => {
      return el[column];
    });
    const minValue = Number(Math.min(...valuesArray)).toFixed(2);
    const maxValue = Number(Math.max(...valuesArray)).toFixed(2);
    const minMaxValues = getMinMaxForFormattedData(minValue, maxValue);

    const finalMinValue = Number(minValue) < 1000 ? minValue : minMaxValues.formattedMinInNumber;
    const finalMaxValue = Number(maxValue) < 1000 ? maxValue : minMaxValues.formattedMaxInNumber;

    return { min: finalMinValue, max: finalMaxValue };
  };

  const combinedThemes = useCombinedColorSchemes();
  const defaultThemeId = useSelector(
    (state: RootState) => state.tabState.tabs[tabId]?.dashboardState.defaultThemeId,
  );

  const defaultThemeName = useMemo(() => {
    let theme = defaultThemeId
      ? combinedThemes.find((t) => t.id === defaultThemeId || t.themeId === defaultThemeId)
      : undefined;

    if (!theme && combinedThemes.length > 0) {
      theme = combinedThemes[0];
    }

    return theme?.name;
  }, [defaultThemeId, combinedThemes]);

  const updateConditionalFormattingMinMax = () => {
    if (['crossTab', 'table'].includes(chartProperties.properties[_propKey].chartType)) {
      let conditionalFormat = JSON.parse(
        JSON.stringify(chartControls.properties[_propKey]?.tableConditionalFormats),
      );
      if (conditionalFormat && conditionalFormat.length > 0) {
        [...conditionalFormat].forEach(async (format: any, index: number) => {
          let names = format.name.split(' of ');
          let name = names.length > 1 ? names[1] : names[0];
          let agg = names.length > 1 ? names[0] : '';
          const columnId = format.columnId;

          let axesAllFields = [];
          let measureFields = [];

          if (chartProperties.properties[_propKey].chartType == 'table') {
            axesAllFields = [...chartProp.chartAxes[1].fields, ...chartProp.chartAxes[2].fields];
            measureFields = chartProp.chartAxes[2].fields;
          } else {
            axesAllFields = [
              ...chartProp.chartAxes[1].fields,
              ...chartProp.chartAxes[2].fields,
              ...chartProp.chartAxes[3].fields,
            ];
            measureFields = chartProp.chartAxes[3].fields;
          }

          let findField = axesAllFields.find((item: any) => {
            if (format.columnId === item.uId) {
              return true;
            }
            return false;
          });

          if (!findField) {
            dispatch(deleteTablecf(_propKey, index));
          } else {
            let findMeasureField = measureFields.find((item: any) => {
              return item.uId === columnId;
            });

            if (!findMeasureField) {
              let _colValues = format.isLabel
                ? await getLabelValues(
                    getNameFromUid(chartProperties?.properties[_propKey]?.chartAxes, columnId),
                    chartControls,
                    chartProperties,
                    _propKey,
                    token,
                    defaultThemeName,
                    combinedThemes,
                  )
                : [];
              format.value = _colValues;
              format.name = displayName(findField);
              dispatch(updatecfObjectOptions(_propKey, index, format));
            } else {
              if (format.isGradient && !format.isUserChanged) {
                let minMaxValue: any = getMinAndMaxValue(displayName(findField));
                let minObject = format.value.find((val: any) => val.name === 'Min'),
                  maxObject = format.value.find((val: any) => val.name === 'Max'),
                  midObject = format.value.find((val: any) => val.name?.trim() === 'Mid Value');

                if (!minObject.isUserChanged) minObject.value = minMaxValue.min;
                if (!maxObject.isUserChanged) maxObject.value = minMaxValue.max;
                if (midObject && !midObject.isUserChanged)
                  midObject.value = (parseFloat(minMaxValue.min) + parseFloat(minMaxValue.max)) / 2;

                format.name = displayName(findField);
                dispatch(updatecfObjectOptions(_propKey, index, format));
              }
            }
          }
        });
      }
    }
  };

  const { loading, message, severity } = useChartData(
    screenFrom,
    tabId,
    chartProp,
    _propKey,
    shareDashboardLink,
    setShowSnackBar,
    setOpenAlert,
    setTestMessage,
  );

  useEffect(() => {
    updateConditionalFormattingMinMax();
    //eslint-disable-next-line
  }, [
    chartControls.properties[_propKey]?.chartData,
    chartControls.properties[_propKey]?.colorScheme,
    defaultThemeName,
  ]);

  // useEffect(() => {
  //   if (chartProp.chartType === 'simplecard') {
  //     if (
  //       chartControls?.properties[_propKey]?.chartData?.length === 0 ||
  //       chartProp.chartAxes[1]?.fields?.length === 0
  //     ) {
  //       dispatch(addOrEditSimplecardConditionalFormat(_propKey, []));
  //     }
  //   }
  // }, [chartControls.properties[_propKey]?.chartData]);

  return (
    <>
      <NotificationDialog
        openAlert={openAlert}
        severity={severity}
        testMessage={testMessage}
        onCloseAlert={() => {
          setOpenAlert(false);
          setTestMessage('');
        }}
      />
      <SnackBar
        show={showSnackBar}
        message={message}
        onClose={() => setShowSnackBar(false)}
        sx={{
          '.MuiSnackbarContent-root': {
            backgroundColor: 'rgb(231, 219, 179)',
            height: '3rem',
            color: 'primary.contrastText',
          },
        }}
        dir={{ vertical: 'top', horizontal: 'right' }}
      />
      <div
        className={`charAxesArea ${screenFrom === 'Dashboard' ? 'no-gutter' : ''}`}
        style={
          screenFrom === 'Dashboard'
            ? {
                minWidth: 0,
                margin: 0,
                scrollbarGutter: 'none',
                border: 'none',
              }
            : {}
        }
      >
        {loading ? <LoadingPopover /> : null}
        {/* {loading ? <PopUpSpinner show={true} /> : null} */}
      </div>
    </>
  );
};

export default ChartData;
